import React from "react";

// Kept identical to career-nine-assessment/src/utils/instructionMarkdown.tsx
// (what the student sees): change both together.

type Block =
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "p"; text: string };

const SPECIAL = /^(#{1,2}\s|[-*]\s|\d+[.)]\s)/;

function isSpecial(line: string): boolean {
  return SPECIAL.test(line.trim());
}

// A line's heading, bullet or number prefix, kept apart from its text.
const LINE_PREFIX = /^(\s*(?:#{1,2}\s+|[-*]\s+|\d+[.)]\s+)?)([\s\S]*)$/;
// A block prefix right after an opening marker ("**- item"): the old editor's
// Bold left lists like that.
const PREFIX_AFTER_MARKER = /^(#{1,2}\s+|-\s+|\d+[.)]\s+)/;

type Piece = { text: string; marker?: string; seq?: number; drop?: boolean };
type MarkedLine = { prefix: string; pieces: Piece[]; before: string[]; after: string[] };

/**
 * Bold and italic that open on one line and close on a later one
 * ("**Read\ncarefully**", or bold over several list items) are closed at each
 * line end and opened again on the next line, so every line carries its own
 * pair. A marker with no partner stays as typed. Underscores are left alone:
 * they are common inside names.
 */
export function closeMarkersPerLine(input: string): string {
  const lines: MarkedLine[] = input.split("\n").map((raw) => {
    const m = LINE_PREFIX.exec(raw);
    const prefix = m ? m[1] : "";
    const content = m ? m[2] : raw;
    const pieces = content
      .split(/(\*\*|\*)/)
      .map((part, i): Piece => (i % 2 ? { text: "", marker: part } : { text: part }));
    return { prefix, pieces, before: [], after: [] };
  });

  // Pair the markers in reading order: bold with bold, italic with italic.
  type At = { line: number; index: number; piece: Piece };
  const open: Record<string, At | undefined> = {};
  const spanning: Array<[At, At]> = [];
  let seq = 0;
  lines.forEach((l, line) => {
    l.pieces.forEach((piece, index) => {
      if (!piece.marker) return;
      piece.seq = seq++;
      const opener = open[piece.marker];
      if (!opener) {
        open[piece.marker] = { line, index, piece };
        return;
      }
      open[piece.marker] = undefined;
      if (opener.line !== line) spanning.push([opener, { line, index, piece }]);
    });
  });
  spanning.sort((a, b) => (a[0].piece.seq || 0) - (b[0].piece.seq || 0));

  const textOf = (pieces: Piece[]) => pieces.map((p) => p.marker || p.text).join("");
  spanning.forEach(([o, c]) => {
    const marker = o.piece.marker as string;
    const first = lines[o.line];
    const next = first.pieces[o.index + 1];
    const moved = PREFIX_AFTER_MARKER.exec(next.text);
    if (moved && !first.prefix.trim() && !textOf(first.pieces.slice(0, o.index)).trim()) {
      first.prefix += moved[1];
      next.text = next.text.slice(moved[1].length);
    }
    if (!textOf(first.pieces.slice(o.index + 1)).trim()) {
      // Nothing after it on its line: it opens on the next line instead.
      o.piece.drop = true;
    } else {
      first.after.unshift(marker);
    }
    for (let i = o.line + 1; i < c.line; i++) {
      if (!textOf(lines[i].pieces).trim()) continue;
      lines[i].before.push(marker);
      lines[i].after.unshift(marker);
    }
    if (!textOf(lines[c.line].pieces.slice(0, c.index)).trim()) {
      // Nothing before it on its line: the line above already closed the pair.
      c.piece.drop = true;
    } else {
      lines[c.line].before.push(marker);
    }
  });

  return lines
    .map((l) => {
      let text = l.pieces.filter((p) => !p.drop).map((p) => p.marker || p.text).join("");
      if (l.after.length) text = text.replace(/\s+$/, "");
      if (l.before.length) text = text.replace(/^\s+/, "");
      return l.prefix + l.before.join("") + text + l.after.join("");
    })
    .join("\n");
}

export function parseInstructionMarkdown(input: string): Block[] {
  const lines = closeMarkersPerLine((input || "").replace(/\r\n/g, "\n")).split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (/^##\s+/.test(trimmed)) {
      blocks.push({ kind: "h3", text: trimmed.replace(/^##\s+/, "") });
      i++;
    } else if (/^#\s+/.test(trimmed)) {
      blocks.push({ kind: "h2", text: trimmed.replace(/^#\s+/, "") });
      i++;
    } else if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ul", items });
    } else if (/^\d+[.)]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ol", items });
    } else {
      const paraLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !isSpecial(lines[i])
      ) {
        paraLines.push(lines[i].trim());
        i++;
      }
      blocks.push({ kind: "p", text: paraLines.join("\n") });
    }
  }

  return blocks;
}

/**
 * Wraps text in a bold or italic marker the way the renderer reads it: each
 * line on its own, after its heading, bullet or number prefix. A pair across
 * a line break would show as bare asterisks.
 */
export function wrapLines(text: string, marker: string): string {
  return text
    .split("\n")
    .map((line) => {
      const m = LINE_PREFIX.exec(line);
      const prefix = m ? m[1] : "";
      const rest = m ? m[2] : line;
      const body = rest.replace(/\s+$/, "");
      if (!body) return line;
      return prefix + marker + body + marker + rest.slice(body.length);
    })
    .join("\n");
}

const INLINE = /(\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_)/g;

function renderInlineSegment(text: string, baseKey: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(INLINE.source, INLINE.flags);
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const k = `${baseKey}-${key++}`;
    if (m[2] !== undefined)
      parts.push(<strong key={k}>{renderInlineSegment(m[2], k)}</strong>);
    else if (m[3] !== undefined)
      parts.push(<em key={k}>{renderInlineSegment(m[3], k)}</em>);
    else if (m[4] !== undefined)
      parts.push(<em key={k}>{m[4]}</em>);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function renderInline(text: string): React.ReactNode[] {
  // Preserve single newlines as <br/> so legacy multi-line text still wraps.
  const lines = text.split("\n");
  const out: React.ReactNode[] = [];
  lines.forEach((line, i) => {
    out.push(...renderInlineSegment(line, `l${i}`));
    if (i < lines.length - 1) out.push(<br key={`br-${i}`} />);
  });
  return out;
}

interface MarkdownInstructionsProps {
  text: string;
  className?: string;
}

export const MarkdownInstructions: React.FC<MarkdownInstructionsProps> = ({
  text,
  className,
}) => {
  const blocks = parseInstructionMarkdown(text);
  return (
    <div className={`im-root ${className || ""}`.trim()}>
      {blocks.map((b, idx) => {
        switch (b.kind) {
          case "h2":
            return (
              <h3 key={idx} className="im-h2">
                {renderInline(b.text)}
              </h3>
            );
          case "h3":
            return (
              <h4 key={idx} className="im-h3">
                {renderInline(b.text)}
              </h4>
            );
          case "ul":
            return (
              <ul key={idx} className="im-ul">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={idx} className="im-ol">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            );
          case "p":
            return (
              <p key={idx} className="im-p">
                {renderInline(b.text)}
              </p>
            );
        }
      })}
    </div>
  );
};
